/**
 * Duplicate Commercial Component (Wrapper)
 *
 * This is a minimal wrapper around the generic DuplicateEntity component
 * that handles commercial property-specific mutation logic.
 *
 * @author Housefolios
 * @created 2025-01-27
 * @modified 2025-08-12
 */

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client/react'
import { enqueueSnackbar } from 'notistack'
import DuplicateEntityComponent from './index.jsx'
import { COMMERCIAL_CONFIG } from './entityConfigs'
import { COPY_COMMERCIAL } from '@/housefolios-components/Commercial/DuplicateCommercial/mutations'
import { GET_COMMERCIAL_PROPERTIES } from '@/housefolios-components/Lots/queries'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { GET_ME } from '@/housefolios-components/Session/queries'

/**
 * Duplicate Commercial Component
 *
 * @param {Object} props - Component props
 * @param {string} props.commercialId - ID of the commercial property to duplicate
 * @param {Object} props.commercial - The commercial property object
 * @param {Array} props.portfolios - Available portfolios
 * @param {string} props.selectedPortfolio - Currently selected portfolio
 * @param {Function} props.setSelected - Function to clear selection after duplication
 * @param {Object} props.session - User session object
 * @param {Function} props.onSuccess - Success callback
 * @param {Function} props.onError - Error callback
 * @param {boolean} props.notifications - Whether to show as notification menu item
 * @param {boolean} props.rightClickMenu - Whether to show as right-click menu item
 * @param {string} props.buttonText - Custom button text
 */
function DuplicateCommercial(props) {
  const {
    commercialId,
    commercial,
    portfolios,
    selectedPortfolio: initialSelectedPortfolio,
    setSelected,
    session,
    onSuccess,
    onError,
    notifications = false,
    rightClickMenu = false,
    buttonText = '',
    ...otherProps
  } = props

  // Local state for portfolio selection
  const [selectedPortfolio, setSelectedPortfolio] = useState(
    initialSelectedPortfolio,
  )

  // GraphQL mutation
  const [copyCommercial] = useMutation(COPY_COMMERCIAL)

  // Handle form submission
  const handleSubmit = async (additionalState = {}) => {
    if (!selectedPortfolio) {
      enqueueSnackbar('Please select a portfolio', {
        variant: 'warning',
      })
      return
    }

    try {
      const result = await copyCommercial({
        awaitRefetchQueries: true,
        variables: {
          commercialId: commercialId,
          portfolioId: selectedPortfolio,
          commercialInput: additionalState, // Pass additional fields
        },
        refetchQueries: [
          {
            query: GET_COMMERCIAL_PROPERTIES,
            variables: { portfolioId: [selectedPortfolio] },
          },
          { query: GET_PORTFOLIOS },
          { query: GET_ME },
        ],
      })

      // Success handling
      enqueueSnackbar(
        'Commercial property was duplicated successfully',
        {
          variant: 'success',
          autoHideDuration: 3000,
        },
      )

      if (setSelected) setSelected([])
      if (onSuccess) onSuccess(result)
    } catch (error) {
      // Error handling
      console.error('Failed to duplicate commercial property:', error)
      enqueueSnackbar('Commercial property failed to duplicate', {
        variant: 'error',
        autoHideDuration: 3000,
      })

      if (onError) onError(error)
    }
  }

  return (
    <DuplicateEntityComponent
      {...otherProps}
      entityType="commercial"
      entityId={commercialId}
      entity={commercial}
      portfolios={portfolios}
      selectedPortfolio={selectedPortfolio}
      setSelected={setSelected}
      session={session}
      config={COMMERCIAL_CONFIG}
      onSuccess={onSuccess}
      onError={onError}
      onSubmit={handleSubmit}
      onPortfolioChange={setSelectedPortfolio}
      notifications={notifications}
      rightClickMenu={rightClickMenu}
      sidebarMenu={false} // Commercial properties don't typically use sidebar menu
      underwriting={false} // Commercial properties don't typically use sidebar menu
      overLimit={false} // Commercial properties don't have limit restrictions
      toggleLimit={() => {}} // No-op function for limit toggle
      buttonText={buttonText}
    />
  )
}

// PropTypes - including rendering props
DuplicateCommercial.propTypes = {
  commercialId: PropTypes.string.isRequired,
  commercial: PropTypes.object,
  portfolios: PropTypes.array.isRequired,
  selectedPortfolio: PropTypes.string,
  setSelected: PropTypes.func,
  session: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  notifications: PropTypes.bool,
  rightClickMenu: PropTypes.bool,
  buttonText: PropTypes.string,
}

export default DuplicateCommercial
